import { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { RESTAURANT_ID } from '../constants';
import { Empty, Icon, Loading, Modal, PageHead, Switch, tl, useToast } from '../ui';

const RESTAURANT_POS = [40.992147, 29.0276927]; // Kadıhan, Kıvanç Sk.
const DEFAULT_ZOOM = 14;
const HEX_R = 200; // metre, flat-top altıgen yarıçapı (uygulama ve web ile aynı)

const ZONE_COLORS = ['#1F4A33', '#B8913A', '#1F5FA8', '#6741A5', '#C0413A', '#00897B', '#A86A00', '#3949AB'];

function getNextColor(existingZones) {
  const used = existingZones.map((z) => z.color).filter(Boolean);
  return ZONE_COLORS.find((c) => !used.includes(c)) || ZONE_COLORS[existingZones.length % ZONE_COLORS.length];
}

const SQRT3 = Math.sqrt(3);
function metersToHex(mx, my) {
  return { q: Math.round((2 / 3 * mx) / HEX_R), r: Math.round((-1 / 3 * mx + SQRT3 / 3 * my) / HEX_R) };
}
function hexToMeters(q, r) {
  return { mx: HEX_R * 3 / 2 * q, my: HEX_R * (SQRT3 / 2 * q + SQRT3 * r) };
}
function hexCornersLatLng(q, r) {
  const { mx, my } = hexToMeters(q, r);
  const pts = [];
  for (let i = 0; i < 6; i += 1) {
    const a = (Math.PI / 3) * i;
    const ll = L.CRS.EPSG3857.unproject(L.point(mx + HEX_R * Math.cos(a), my + HEX_R * Math.sin(a)));
    pts.push([ll.lat, ll.lng]);
  }
  return pts;
}
function latLngToMeters(lat, lng) {
  const p = L.CRS.EPSG3857.project(L.latLng(lat, lng));
  return { mx: p.x, my: p.y };
}
const hexId = (q, r) => `${q}_${r}`;
const parseHex = (id) => { const [q, r] = id.split('_').map((n) => parseInt(n, 10)); return Number.isNaN(q) || Number.isNaN(r) ? null : { q, r }; };

const EMPTY_FORM = { name: '', color: ZONE_COLORS[0], estimatedMinutes: 30, deliveryFee: 0, minOrderAmount: 0, isActive: true };

function DeliveryZones() {
  const toast = useToast();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | edit
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState(EMPTY_FORM);
  const [selectedHexes, setSelectedHexes] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tool, setTool] = useState('paint'); // pan | paint | erase
  const toolRef = useRef(tool);
  useEffect(() => { toolRef.current = tool; }, [tool]);
  const painting = useRef(false);

  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const zoneLayers = useRef([]);
  const selLayers = useRef({});
  const modeRef = useRef(mode);
  const zonesRef = useRef(zones);
  const editingRef = useRef(editingZone);
  const selectedRef = useRef(selectedHexes);
  const colorRef = useRef(zoneForm.color);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { zonesRef.current = zones; }, [zones]);
  useEffect(() => { editingRef.current = editingZone; }, [editingZone]);
  useEffect(() => { selectedRef.current = selectedHexes; }, [selectedHexes]);
  useEffect(() => { colorRef.current = zoneForm.color; }, [zoneForm.color]);

  const loadZones = async () => {
    try {
      const snap = await getDocs(collection(db, 'restaurants', RESTAURANT_ID, 'deliveryZones'));
      setZones(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); toast('Bölgeler yüklenemedi.', 'error'); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadZones(); }, []);

  // ── Harita ────────────────────────────────────────────────────────
  const drawSelection = () => {
    const map = mapObj.current;
    if (!map) return;
    Object.values(selLayers.current).forEach((l) => map.removeLayer(l));
    selLayers.current = {};
    selectedRef.current.forEach((id) => {
      const h = parseHex(id);
      if (!h) return;
      const poly = L.polygon(hexCornersLatLng(h.q, h.r), { color: colorRef.current, fillColor: colorRef.current, fillOpacity: 0.5, weight: 2, dashArray: '4 3' }).addTo(map);
      poly.on('click', (e) => { L.DomEvent.stopPropagation(e); if (toolRef.current === 'pan') toggleHex(id); });
      selLayers.current[id] = poly;
    });
  };

  const drawZones = () => {
    const map = mapObj.current;
    if (!map) return;
    zoneLayers.current.forEach((l) => map.removeLayer(l));
    zoneLayers.current = [];
    zonesRef.current.forEach((zone) => {
      if (modeRef.current === 'edit' && editingRef.current?.id === zone.id) return; // düzenlenen bölge seçim olarak çizilir
      (zone.hexIds || []).forEach((id) => {
        const h = parseHex(id);
        if (!h) return;
        const color = zone.color || ZONE_COLORS[0];
        const poly = L.polygon(hexCornersLatLng(h.q, h.r), { color, fillColor: color, fillOpacity: zone.isActive !== false ? 0.3 : 0.08, weight: 1 }).addTo(map);
        poly.bindTooltip(`<b>${zone.name}</b><br/>${zone.estimatedMinutes || 30} dk · ${zone.deliveryFee > 0 ? `${zone.deliveryFee} TL` : 'ücretsiz'}`, { sticky: true });
        poly.on('click', (e) => { L.DomEvent.stopPropagation(e); if (modeRef.current !== 'edit') startEdit(zone); });
        zoneLayers.current.push(poly);
      });
    });
  };

  const toggleHex = (id, force) => {
    if (modeRef.current !== 'edit') return;
    if (force === 'add' && selectedRef.current.has(id)) return;
    if (force === 'remove' && !selectedRef.current.has(id)) return;
    const owner = zonesRef.current.find((z) => z.id !== editingRef.current?.id && (z.hexIds || []).includes(id));
    if (owner) { if (!force) toast(`Bu alan zaten “${owner.name}” bölgesinde.`, 'error'); return; }
    const next = new Set(selectedRef.current);
    if (next.has(id)) next.delete(id); else next.add(id);
    selectedRef.current = next;
    setSelectedHexes(next);
    drawSelection();
  };

  useEffect(() => {
    if (loading || !mapRef.current || mapObj.current) return undefined;
    const map = L.map(mapRef.current, { zoomControl: true }).setView(RESTAURANT_POS, DEFAULT_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
    L.circleMarker(RESTAURANT_POS, { radius: 8, color: '#fff', weight: 3, fillColor: '#B8913A', fillOpacity: 1 })
      .addTo(map).bindTooltip('Kadıhan', { permanent: true, direction: 'top', offset: [0, -8] });
    const hexAt = (latlng) => { const { mx, my } = latLngToMeters(latlng.lat, latlng.lng); const { q, r } = metersToHex(mx, my); return hexId(q, r); };
    const brush = () => (toolRef.current === 'erase' ? 'remove' : 'add');
    map.on('click', (e) => { if (modeRef.current === 'edit' && toolRef.current === 'pan') toggleHex(hexAt(e.latlng)); });
    map.on('mousedown', (e) => {
      if (modeRef.current !== 'edit' || toolRef.current === 'pan') return;
      painting.current = true;
      toggleHex(hexAt(e.latlng), brush());
    });
    map.on('mousemove', (e) => { if (painting.current) toggleHex(hexAt(e.latlng), brush()); });
    const stop = () => { painting.current = false; };
    map.on('mouseup', stop);
    map.on('mouseout', stop);
    mapObj.current = map;
    drawZones();
    return () => { map.remove(); mapObj.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { drawZones(); drawSelection(); }, [zones, mode, editingZone, zoneForm.color]);

  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    if (mode === 'edit' && tool !== 'pan') map.dragging.disable(); else map.dragging.enable();
    map.getContainer().style.cursor = mode === 'edit' && tool !== 'pan' ? 'crosshair' : '';
  }, [mode, tool]);

  // ── Düzenleme akışı ───────────────────────────────────────────────
  const startNew = () => {
    setEditingZone(null);
    editingRef.current = null;
    setZoneForm({ ...EMPTY_FORM, color: getNextColor(zones), name: `Bölge ${zones.length + 1}` });
    const empty = new Set();
    selectedRef.current = empty;
    setSelectedHexes(empty);
    setTool('paint');
    setMode('edit');
    if (mapObj.current && mapObj.current.getZoom() < 15) mapObj.current.setView(RESTAURANT_POS, 15);
  };

  const startEdit = (zone) => {
    setEditingZone(zone);
    editingRef.current = zone;
    setZoneForm({
      name: zone.name || '', color: zone.color || ZONE_COLORS[0],
      estimatedMinutes: zone.estimatedMinutes ?? 30, deliveryFee: zone.deliveryFee ?? 0,
      minOrderAmount: zone.minOrderAmount ?? 0, isActive: zone.isActive !== false,
    });
    const set = new Set(zone.hexIds || []);
    selectedRef.current = set;
    setSelectedHexes(set);
    setMode('edit');
    const first = (zone.hexIds || []).map(parseHex).find(Boolean);
    if (first && mapObj.current) {
      const bounds = L.latLngBounds((zone.hexIds || []).map(parseHex).filter(Boolean).flatMap((h) => hexCornersLatLng(h.q, h.r)));
      mapObj.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  };

  const cancelEdit = () => {
    setMode('list');
    setEditingZone(null);
    editingRef.current = null;
    const empty = new Set();
    selectedRef.current = empty;
    setSelectedHexes(empty);
  };

  const saveZone = async () => {
    if (!zoneForm.name.trim()) { toast('Bölge adı zorunlu.', 'error'); return; }
    if (selectedHexes.size === 0) { toast('Haritadan en az bir alan seçin.', 'error'); return; }
    setSaving(true);
    const data = {
      name: zoneForm.name.trim(),
      color: zoneForm.color,
      estimatedMinutes: parseInt(zoneForm.estimatedMinutes, 10) || 30,
      deliveryFee: parseFloat(zoneForm.deliveryFee) || 0,
      minOrderAmount: parseFloat(zoneForm.minOrderAmount) || 0,
      isActive: zoneForm.isActive,
      hexIds: Array.from(selectedHexes),
      hexR: HEX_R,
    };
    try {
      if (editingZone) await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'deliveryZones', editingZone.id), data);
      else await addDoc(collection(db, 'restaurants', RESTAURANT_ID, 'deliveryZones'), data);
      toast(editingZone ? 'Bölge güncellendi.' : 'Bölge oluşturuldu.', 'ok');
      cancelEdit();
      await loadZones();
    } catch (e) { console.error(e); toast('Kaydedilemedi, tekrar deneyin.', 'error'); }
    setSaving(false);
  };

  const removeZone = async (zone) => {
    setConfirmDelete(null);
    try {
      await deleteDoc(doc(db, 'restaurants', RESTAURANT_ID, 'deliveryZones', zone.id));
      toast('Bölge silindi.', 'ok');
      cancelEdit();
      await loadZones();
    } catch (e) { console.error(e); toast('Silinemedi.', 'error'); }
  };

  const toggleActive = async (zone) => {
    const next = zone.isActive === false;
    setZones((list) => list.map((z) => (z.id === zone.id ? { ...z, isActive: next } : z)));
    try { await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'deliveryZones', zone.id), { isActive: next }); }
    catch { toast('Güncellenemedi.', 'error'); loadZones(); }
  };

  if (loading) return <div className="k-page"><Loading /></div>;

  const areaKm2 = (n) => ((n * 3 * SQRT3 / 2 * HEX_R * HEX_R) / 1e6).toFixed(2);

  return (
    <div className="k-page">
      <PageHead desc="Teslimat yapılan alanları haritadan seçin; her bölgenin ücreti, minimum sepeti ve süresi ayrı olabilir." />

      <div className="kz-layout">
        <aside className="kz-panel k-card">
          {mode === 'list' ? (
            <>
              <div className="kz-panel-head">
                <div><strong>Bölgeler</strong><span>{zones.length} bölge</span></div>
                <button type="button" className="k-btn k-btn-primary k-btn-sm" onClick={startNew}><Icon name="plus" size={14} />Yeni bölge</button>
              </div>
              {zones.length === 0 ? (
                <Empty icon="map" title="Henüz bölge yok">“Yeni bölge”ye basıp haritadan teslimat yapacağınız alanları seçin. Bölge yokken yalnızca gel-al siparişi alınır.</Empty>
              ) : (
                <ul className="kz-list">
                  {zones.map((z) => (
                    <li key={z.id} className={z.isActive === false ? 'off' : ''}>
                      <span className="kz-swatch" style={{ background: z.color || ZONE_COLORS[0] }} />
                      <button type="button" className="kz-item" onClick={() => startEdit(z)}>
                        <strong>{z.name}</strong>
                        <span>{z.deliveryFee > 0 ? tl(z.deliveryFee) : 'Ücretsiz'} · min. {tl(z.minOrderAmount || 0)} · {z.estimatedMinutes || 30} dk</span>
                        <em>{(z.hexIds || []).length} alan · ~{areaKm2((z.hexIds || []).length)} km²</em>
                      </button>
                      <Switch checked={z.isActive !== false} onChange={() => toggleActive(z)} title={z.isActive !== false ? 'Pasif yap' : 'Aktif yap'} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="kz-form">
              <div className="kz-panel-head">
                <div><strong>{editingZone ? 'Bölgeyi düzenle' : 'Yeni bölge'}</strong><span>Haritanın üstündeki araçlarla alan ekleyin veya çıkarın</span></div>
              </div>
              <div className="kz-count"><Icon name="map" size={16} />{selectedHexes.size} alan seçili · ~{areaKm2(selectedHexes.size)} km²</div>
              <label className="k-field"><span>Bölge adı</span><input className="k-input" value={zoneForm.name} onChange={(e) => setZoneForm((p) => ({ ...p, name: e.target.value }))} placeholder="Örn. Moda, Yakın çevre" /></label>
              <div className="k-field"><span>Renk</span>
                <div className="kz-colors">
                  {ZONE_COLORS.map((c) => <button key={c} type="button" aria-label={c} className={zoneForm.color === c ? 'on' : ''} style={{ background: c }} onClick={() => setZoneForm((p) => ({ ...p, color: c }))} />)}
                </div>
              </div>
              <div className="kz-grid">
                <label className="k-field"><span>Teslimat ücreti (TL)</span><input className="k-input" type="number" min="0" value={zoneForm.deliveryFee} onChange={(e) => setZoneForm((p) => ({ ...p, deliveryFee: e.target.value }))} /></label>
                <label className="k-field"><span>Min. sepet (TL)</span><input className="k-input" type="number" min="0" value={zoneForm.minOrderAmount} onChange={(e) => setZoneForm((p) => ({ ...p, minOrderAmount: e.target.value }))} /></label>
                <label className="k-field"><span>Süre (dk)</span><input className="k-input" type="number" min="1" value={zoneForm.estimatedMinutes} onChange={(e) => setZoneForm((p) => ({ ...p, estimatedMinutes: e.target.value }))} /></label>
              </div>
              <div style={{ marginBottom: 16 }}><Switch checked={zoneForm.isActive} onChange={(v) => setZoneForm((p) => ({ ...p, isActive: v }))} label="Bölge aktif (sipariş alınır)" /></div>
              <div className="kz-actions">
                <button type="button" className="k-btn k-btn-primary" onClick={saveZone} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
                <button type="button" className="k-btn k-btn-secondary" onClick={cancelEdit}>Vazgeç</button>
                {editingZone && <button type="button" className="k-icon-btn kz-del" title="Bölgeyi sil" onClick={() => setConfirmDelete(editingZone)}><Icon name="trash" size={16} /></button>}
              </div>
            </div>
          )}
        </aside>

        <div className="kz-map-wrap k-card">
          <div ref={mapRef} className="kz-map" />
          {mode === 'edit' && (
            <div className="kz-tools">
              {[['pan', 'Taşı'], ['paint', 'Boya'], ['erase', 'Sil']].map(([id, label]) => (
                <button key={id} type="button" className={tool === id ? 'on' : ''} onClick={() => setTool(id)}>{label}</button>
              ))}
              <span>{tool === 'pan' ? 'Haritayı sürükleyin; tıklayınca alan eklenir/çıkar' : tool === 'paint' ? 'Basılı tutup sürükleyerek alan ekleyin' : 'Basılı tutup sürükleyerek alan çıkarın'}</span>
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <Modal
          title="Bölgeyi sil"
          width={420}
          onClose={() => setConfirmDelete(null)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setConfirmDelete(null)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-danger-solid" onClick={() => removeZone(confirmDelete)}>Sil</button>
            </>
          )}
        >
          “{confirmDelete.name}” silinecek. Bu bölgedeki adreslere teslimat yapılamaz hale gelir.
        </Modal>
      )}

      <style>{`
        .kz-layout { display: grid; grid-template-columns: 340px minmax(0, 1fr); gap: 16px; align-items: start; }
        .kz-panel { padding: 14px; position: sticky; top: 16px; max-height: calc(100vh - 150px); overflow: auto; }
        .kz-panel-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
        .kz-panel-head strong { display: block; font-size: 15px; color: var(--k-ink); }
        .kz-panel-head span { font-size: 12.5px; color: var(--k-muted); }
        .kz-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
        .kz-list li { display: flex; align-items: center; gap: 10px; padding: 10px; border: 1px solid var(--k-line); border-radius: 6px; }
        .kz-list li.off { opacity: .6; }
        .kz-swatch { width: 12px; height: 36px; border-radius: 4px; flex: none; }
        .kz-item { flex: 1; min-width: 0; text-align: left; border: 0; background: none; padding: 0; display: grid; gap: 1px; }
        .kz-item strong { color: var(--k-ink); font-size: 14px; }
        .kz-item span { font-size: 12.5px; color: var(--k-text); }
        .kz-item em { font-style: normal; font-size: 12px; color: var(--k-muted); }
        .kz-count { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 9px; background: var(--k-green-soft); color: var(--k-green-dark); font-weight: 600; font-size: 13px; margin-bottom: 14px; }
        .kz-colors { display: flex; gap: 8px; flex-wrap: wrap; }
        .kz-colors button { width: 28px; height: 28px; border-radius: 8px; border: 3px solid transparent; }
        .kz-colors button.on { border-color: var(--k-ink); box-shadow: 0 0 0 2px #fff inset; }
        .kz-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 10px; }
        .kz-grid .k-field:last-child { grid-column: 1 / -1; }
        .kz-actions { display: flex; gap: 8px; align-items: center; }
        .kz-actions .k-btn-primary { flex: 1; }
        .kz-del { color: var(--k-red); margin-left: auto; }
        .kz-map-wrap { position: relative; overflow: hidden; }
        .kz-map { height: calc(100vh - 170px); min-height: 480px; }
        .kz-tools { position: absolute; left: 50%; top: 12px; transform: translateX(-50%); z-index: 500; display: flex; align-items: center; gap: 4px; padding: 4px; background: #fff; border-radius: 6px; box-shadow: var(--k-shadow-lg); }
        .kz-tools button { height: 32px; padding: 0 14px; border: 0; border-radius: 9px; background: none; font-weight: 600; font-size: 13px; color: var(--k-text); }
        .kz-tools button.on { background: var(--k-green); color: #fff; }
        .kz-tools span { font-size: 12px; color: var(--k-muted); padding: 0 10px 0 6px; white-space: nowrap; }
        @media (max-width: 1100px) { .kz-layout { grid-template-columns: 1fr; } .kz-panel { position: static; max-height: none; } .kz-map { height: 60vh; } }
      `}</style>
    </div>
  );
}

export default DeliveryZones;
